import { type CalculatorItem } from '@/pages/calculadora/hooks/useCalculadora';
import { useEffect, useState, type ReactNode } from 'react';

type HelpInfo = { title: string; description: string; source: string; calculation: string; calculated?: boolean };

const helpInfo: Record<string, HelpInfo> = {
  numero: { title: 'Número do item', description: 'Posição do produto dentro da cotação.', source: 'Gerado pela interface.', calculation: 'Ordem sequencial dos produtos adicionados.', calculated: true },
  codigo: { title: 'Código', description: 'Código único do material.', source: 'Lido do cadastro de materiais no CIGAM.', calculation: 'Não possui cálculo.' },
  descricao: { title: 'Descrição', description: 'Nome comercial do material.', source: 'Lido do cadastro de materiais no CIGAM.', calculation: 'Não possui cálculo.' },
  peso: { title: 'Peso', description: 'Peso do item em quilos (kg), informado pelo vendedor para esta cotação.', source: 'Digitado na calculadora.', calculation: 'Não possui cálculo. O valor segue para a cotação salva, o PDF e o e-mail. Campo opcional.' },
  custo: { title: 'Custo', description: 'Custo de reposição convertido para a moeda selecionada.', source: 'Custo lido do CIGAM e câmbio PTAX do sistema.', calculation: 'Custo em BRL ÷ cotação da moeda selecionada.', calculated: true },
  precoMin: { title: 'Preço mínimo NET', description: 'Preço NET mínimo para atingir a margem-base de 40%.', source: 'Calculado pelo sistema a partir do custo.', calculation: 'Custo convertido ÷ 0,60.', calculated: true },
  icms: { title: 'ICMS', description: 'Alíquota aplicada conforme UF, origem fiscal e isenção.', source: 'Cadastro do material e UF do cliente.', calculation: 'RS usa a alíquota do material; origens 1, 2, 3 e 8 usam 4%; demais UFs usam 7% ou 12%. Isenção e exterior resultam em 0%.', calculated: true },
  ipi: { title: 'IPI', description: 'Alíquota de IPI usada na cotação.', source: 'Lida do cadastro fiscal do material.', calculation: 'Alíquota cadastrada ÷ 100; com isenção, o resultado é 0%.', calculated: true },
  fator: { title: 'Fator', description: 'Fator fiscal usado para transformar o preço NET em preço com impostos sem IPI.', source: 'Calculado pelo sistema conforme ICMS e PIS/COFINS.', calculation: 'Tabela de fatores: 0%=0,9075; 4%=0,8712; 7%=0,8440; 12%=0,7986; 18%=0,7442. Com isenção de PIS/COFINS: 1 − ICMS.', calculated: true },
  precoMinImpostos: { title: 'Preço mínimo com impostos sem IPI', description: 'Preço mínimo acrescido dos impostos considerados, sem IPI.', source: 'Calculado pelo sistema.', calculation: 'Preço mínimo NET ÷ fator.', calculated: true },
  negociadoNet: { title: 'Negociado NET', description: 'Preço NET informado pelo usuário na moeda selecionada.', source: 'Digitado na calculadora.', calculation: 'Inicia com o preço mínimo NET e pode ser alterado manualmente.' },
  negociadoImpostos: { title: 'Negociado com impostos sem IPI', description: 'Preço negociado após aplicação do fator fiscal.', source: 'Calculado pelo sistema.', calculation: 'Preço negociado NET ÷ fator.', calculated: true },
  margem: { title: 'Margem', description: 'Margem obtida com o preço negociado.', source: 'Calculada com custo e preço negociado.', calculation: '(1 − custo convertido ÷ preço negociado NET) × 100.', calculated: true },
  tabelaNet: { title: 'Tabela CIGAM NET', description: 'Último preço NET encontrado para o cliente e material.', source: 'Lido da Tabela de Preços do CIGAM.', calculation: 'Associação por código do cliente e código/descrição do material.' },
  margemTabela: { title: 'Margem da tabela CIGAM', description: 'Margem implícita do preço existente na tabela.', source: 'Preço do CIGAM combinado com custo e câmbio.', calculation: '(1 − custo na moeda da tabela ÷ preço NET da tabela) × 100.', calculated: true },
  estoque: { title: 'Estoque', description: 'Quantidade disponível do material, somando todos os lotes.', source: 'Estoque.csv do CIGAM, enviado por e-mail.', calculation: 'Soma da coluna QTD de todos os lotes. Sem carga ainda, exibe “Consultar”.' },
  usd: { title: 'Equivalente USD', description: 'Preço negociado convertido para dólar.', source: 'Preço negociado e câmbio PTAX atualizado.', calculation: '(Preço negociado × câmbio da moeda selecionada) ÷ PTAX do dólar.', calculated: true },
};

function HelpHeader({ helpKey, children, onHelp, align = 'right' }: { helpKey: string; children: ReactNode; onHelp: (key: string) => void; align?: 'left' | 'right' }) {
  return <span className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : 'justify-start'}`}><span>{children}</span><button type="button" onClick={() => onHelp(helpKey)} className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[11px] text-primary-600 hover:bg-primary-100" aria-label={`Ajuda sobre ${helpInfo[helpKey].title}`} title="Entenda esta coluna"><i className="ri-question-line" /></button></span>;
}

interface ItensTableProps {
  itens: CalculatorItem[];
  moeda: 'BRL' | 'USD' | 'EUR';
  margemAlvo: number;
  canSeeSensitive: boolean;
  canSeeCostDetails: boolean;
  onUpdatePrecoNegociado: (index: number, value: number) => void;
  onUpdatePeso: (index: number, value: number | null) => void;
  onRemoveItem: (index: number) => void;
}

const formatCurrency = (val: number) =>
  val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function MargemGauge({ value, alvo, showValue }: { value: number; alvo: number; showValue: boolean }) {
  const pct = Math.min(Math.max(value / (alvo * 2 || 80), 0), 1) * 100;
  const color =
    value >= alvo
      ? 'bg-emerald-500'
      : value >= alvo - 5
        ? 'bg-amber-400'
        : 'bg-red-400';

  if (!showValue) {
    const label = value >= alvo ? 'Preço aprovado' : value >= alvo - 5 ? 'Preço em atenção' : 'Preço abaixo do mínimo';
    return <span className={`inline-block h-4 w-4 rounded-full ${color} ring-4 ring-current/10`} title={label} role="img" aria-label={label} />;
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-background-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-[11px] font-semibold font-mono whitespace-normal leading-tight ${
        value >= alvo ? 'text-emerald-600' : value >= alvo - 5 ? 'text-amber-600' : 'text-red-500'
      }`}>
        {value.toFixed(1)}%
      </span>
    </div>
  );
}

function NegotiatedPriceInput({
  value,
  onChange,
  className,
}: {
  value: number;
  onChange: (value: number) => void;
  className: string;
}) {
  const [draft, setDraft] = useState(String(value));
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing && draft !== '') setDraft(String(value));
  }, [value, editing, draft]);

  return (
    <input
      type="number"
      inputMode="decimal"
      step="0.01"
      min="0"
      value={draft}
      aria-label="Preço negociado NET"
      onFocus={() => setEditing(true)}
      onBlur={() => setEditing(false)}
      onChange={(event) => {
        const rawValue = event.target.value;
        setDraft(rawValue);
        if (rawValue === '') {
          onChange(0);
          return;
        }
        const parsedValue = Number(rawValue);
        if (Number.isFinite(parsedValue) && parsedValue >= 0) onChange(parsedValue);
      }}
      className={className}
    />
  );
}

// Peso em kg: campo opcional (vazio = não informado), aceita vírgula ou ponto como separador decimal.
function PesoInput({ value, onChange, className }: { value: number | null; onChange: (value: number | null) => void; className: string }) {
  const [draft, setDraft] = useState(value == null ? '' : String(value).replace('.', ','));
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(value == null ? '' : String(value).replace('.', ','));
  }, [value, editing]);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      placeholder="0"
      aria-label="Peso em kg"
      onFocus={() => setEditing(true)}
      onBlur={() => setEditing(false)}
      onChange={(event) => {
        const raw = event.target.value;
        if (!/^\d*[.,]?\d{0,3}$/.test(raw)) return;
        setDraft(raw);
        const parsed = Number(raw.replace(',', '.'));
        onChange(raw === '' || !Number.isFinite(parsed) || parsed <= 0 ? null : parsed);
      }}
      className={className}
    />
  );
}

export default function ItensTable({
  itens,
  moeda,
  margemAlvo,
  canSeeSensitive,
  canSeeCostDetails,
  onUpdatePrecoNegociado,
  onUpdatePeso,
  onRemoveItem,
}: ItensTableProps) {
  const [activeHelp, setActiveHelp] = useState<HelpInfo | null>(null);
  useEffect(() => {
    if (!activeHelp) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setActiveHelp(null); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [activeHelp]);
  const openHelp = (key: string) => setActiveHelp(helpInfo[key]);

  if (itens.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-dashed border-background-300/60 p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
          <i className="ri-shopping-bag-3-line text-foreground-400 text-xl" />
        </div>
        <p className="text-foreground-600 text-sm font-medium">Nenhum item na cotação</p>
        <p className="text-foreground-400 text-xs mt-1">
          Busque e adicione produtos acima para começar
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[1441px] table-fixed text-[11px] leading-tight">
          <thead>
            <tr className="border-b border-background-200/70 bg-background-50/50">
              <th className="w-[42px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="numero" onHelp={openHelp} align="left">#</HelpHeader></th>
              <th className="w-[92px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="codigo" onHelp={openHelp} align="left">Código</HelpHeader></th>
              <th className="w-[150px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="descricao" onHelp={openHelp} align="left">Descrição</HelpHeader></th>
              <th className="w-[96px] px-2 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="peso" onHelp={openHelp}>Peso</HelpHeader></th>
              {canSeeCostDetails && <th className="w-[72px] px-2 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="custo" onHelp={openHelp}>Custo</HelpHeader></th>}
              <th className="w-[78px] px-1.5 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="precoMin" onHelp={openHelp}><span>Preço mín.<br />NET</span></HelpHeader></th>
              <th className="w-[58px] px-1.5 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="icms" onHelp={openHelp}>ICMS</HelpHeader></th>
              <th className="w-[55px] px-1.5 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="ipi" onHelp={openHelp}>IPI</HelpHeader></th>
              {canSeeCostDetails && <th className="w-[65px] px-1.5 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="fator" onHelp={openHelp}>Fator</HelpHeader></th>}
              <th className="w-[96px] px-1.5 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="precoMinImpostos" onHelp={openHelp}><span>Preço mín.<br />c/ impostos<br />s/IPI</span></HelpHeader></th>
              <th className="w-[100px] px-1.5 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="negociadoNet" onHelp={openHelp}><span>Negociado<br />NET ({moeda})</span></HelpHeader></th>
              <th className="w-[98px] px-1.5 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="negociadoImpostos" onHelp={openHelp}><span>Negociado<br />c/ impostos<br />s/IPI</span></HelpHeader></th>
              <th className="w-[116px] px-2 py-2.5 text-left text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="margem" onHelp={openHelp} align="left">Margem</HelpHeader></th>
              <th className="w-[110px] px-2 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="tabelaNet" onHelp={openHelp}><span>Tabela CIGAM<br />NET</span></HelpHeader></th>
              <th className="w-[98px] px-1.5 py-2.5 text-left text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="margemTabela" onHelp={openHelp} align="left"><span>Margem<br />Tabela<br />CIGAM</span></HelpHeader></th>
              <th className="w-[74px] px-1 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="estoque" onHelp={openHelp}><span>Estoque</span></HelpHeader></th>
              <th className="w-[92px] px-2 py-2.5 text-right text-[10px] font-semibold uppercase leading-tight tracking-wide text-foreground-500"><HelpHeader helpKey="usd" onHelp={openHelp}><span>Equiv.<br />USD</span></HelpHeader></th>
              <th className="w-10 px-2 py-3" />
            </tr>
          </thead>
          <tbody>
            {itens.map((item, idx) => (
              <tr
                key={`${item.materialCodigo}-${idx}`}
                className="border-b border-background-100 last:border-b-0 hover:bg-background-50/50 transition-colors"
              >
                <td className="px-2 py-2.5 text-[11px] text-foreground-500 font-mono">{idx + 1}</td>
                <td className="px-2 py-2.5">
                  <span className="break-all rounded bg-background-100 px-1.5 py-0.5 font-mono text-[11px] leading-tight text-foreground-600">
                    {item.materialCodigo}
                  </span>
                </td>
                <td className="px-2 py-2.5 font-medium text-[11px] leading-tight text-foreground-900 whitespace-normal break-words">
                  {item.materialDescricao}
                </td>
                <td className="px-2 py-2.5">
                  <span className="flex items-center justify-end gap-1">
                    <PesoInput
                      value={item.peso}
                      onChange={(value) => onUpdatePeso(idx, value)}
                      className="w-full min-w-0 text-right px-1.5 py-1.5 text-[11px] leading-tight font-mono font-semibold text-foreground-950 bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50 transition-all"
                    />
                    <span className="text-[10px] font-semibold text-foreground-500">kg</span>
                  </span>
                </td>
                {canSeeCostDetails && <td className="px-2 py-2.5 text-right text-[11px] text-foreground-700 font-mono whitespace-normal break-all">
                  {formatCurrency(item.custoReposicao)}
                </td>}
                <td className="px-2 py-2.5 text-right text-[11px] text-foreground-700 font-mono whitespace-normal break-all">
                  {formatCurrency(item.precoNetMinimo)}
                </td>
                <td className="px-1.5 py-2.5 text-right text-[11px] text-foreground-600 font-mono">{(item.icms * 100).toFixed(0)}%</td>
                <td className="px-1.5 py-2.5 text-right text-[11px] text-foreground-600 font-mono">{(item.ipi * 100).toFixed(2)}%</td>
                {canSeeCostDetails && <td className="px-1.5 py-2.5 text-right text-[11px] text-foreground-600 font-mono whitespace-normal break-all">{(item.fator * 100).toFixed(2)}%</td>}
                <td className="px-1.5 py-2.5 text-right text-[11px] text-foreground-600 font-mono whitespace-normal break-all">
                  {formatCurrency(item.precoComImpostosMinimo)}
                </td>
                <td className="px-1.5 py-2.5 text-right">
                  <NegotiatedPriceInput
                    value={item.precoNegociado}
                    onChange={(value) => onUpdatePrecoNegociado(idx, value)}
                    className="w-full min-w-0 text-right px-1.5 py-1.5 text-[11px] leading-tight font-mono font-semibold text-foreground-950 bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50 transition-all"
                  />
                </td>
                <td className="px-1.5 py-2.5 text-right text-[11px] text-foreground-700 font-mono whitespace-normal break-all">
                  {formatCurrency(item.precoNegociadoComImpostos)}
                </td>
                <td className="px-2 py-2.5">
                  <MargemGauge value={item.margemRealizada} alvo={margemAlvo} showValue={canSeeSensitive} />
                </td>
                <td className="px-2 py-2.5 text-right text-[11px] font-semibold text-foreground-700 font-mono whitespace-normal break-words">
                  {item.precoTabelaNet == null ? '—' : `${item.moedaTabela} ${formatCurrency(item.precoTabelaNet)}`}
                </td>
                <td className="px-2 py-2.5">
                  {item.margemTabela == null ? <span className="text-[11px] leading-tight text-foreground-400">Consultar</span> : <MargemGauge value={item.margemTabela} alvo={margemAlvo} showValue={canSeeSensitive} />}
                </td>
                <td className="px-2 py-2.5 text-right text-[11px] text-foreground-700 font-mono whitespace-normal break-words">
                  {item.estoque == null ? 'Consultar' : formatCurrency(item.estoque)}
                </td>
                <td className="px-2 py-2.5 text-right text-[11px] font-semibold text-foreground-950 font-mono whitespace-normal break-words">
                  US$ {formatCurrency(item.precoUnitarioUsd)}
                </td>
                <td className="px-2 py-3">
                  <button
                    onClick={() => onRemoveItem(idx)}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                    title="Remover item"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {activeHelp && <div className="fixed inset-0 z-[110] flex items-center justify-center bg-primary-950/55 p-4 backdrop-blur-sm" onMouseDown={(event) => { if (event.currentTarget === event.target) setActiveHelp(null); }} role="presentation"><div role="dialog" aria-modal="true" aria-labelledby="calculator-help-title" className="w-full max-w-lg overflow-hidden rounded-2xl border border-white/20 bg-white shadow-2xl"><div className="flex items-start justify-between bg-gradient-to-r from-primary-800 to-primary-600 px-6 py-5 text-white"><div><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/65">Entenda a coluna</p><h2 id="calculator-help-title" className="mt-1 text-xl font-extrabold">{activeHelp.title}</h2></div><button type="button" onClick={() => setActiveHelp(null)} className="flex h-9 w-9 items-center justify-center rounded-lg text-xl hover:bg-white/15" aria-label="Fechar"><i className="ri-close-line" /></button></div><div className="space-y-4 p-6"><span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold ${activeHelp.calculated ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}><i className={activeHelp.calculated ? 'ri-function-line' : 'ri-file-excel-2-line'} />{activeHelp.calculated ? 'Calculado ou derivado pelo sistema' : 'Lido da planilha / informado pelo usuário'}</span><p className="text-sm leading-relaxed text-foreground-700">{activeHelp.description}</p><div className="rounded-xl border border-background-200 bg-background-50 p-4"><p className="text-[10px] font-extrabold uppercase tracking-wider text-primary-600">Origem</p><p className="mt-1.5 text-sm leading-relaxed text-foreground-800">{activeHelp.source}</p></div><div className="rounded-xl border border-background-200 p-4"><p className="text-[10px] font-extrabold uppercase tracking-wider text-primary-600">Cálculo ou regra</p><p className="mt-1.5 text-sm leading-relaxed text-foreground-800">{activeHelp.calculation}</p></div><button type="button" onClick={() => setActiveHelp(null)} className="w-full rounded-xl bg-primary-700 px-4 py-3 text-sm font-bold text-white hover:bg-primary-800">Entendi</button></div></div></div>}

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-background-100">
        {itens.map((item, idx) => (
          <div key={`${item.materialCodigo}-${idx}`} className="p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded">
                    {item.materialCodigo}
                  </span>
                  <span className="text-xs text-foreground-400">#{idx + 1}</span>
                </div>
                <p className="text-sm font-semibold text-foreground-950">{item.materialDescricao}</p>
              </div>
              <button
                onClick={() => onRemoveItem(idx)}
                className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              {canSeeCostDetails && <div>
                <span className="text-foreground-500">Custo:</span>{' '}
                <span className="font-mono text-foreground-800">{formatCurrency(item.custoReposicao)}</span>
              </div>}
              <div>
                <span className="text-foreground-500">Preço mín. NET:</span>{' '}
                <span className="font-mono text-foreground-800">{formatCurrency(item.precoNetMinimo)}</span>
              </div>
              <div>
                <span className="text-foreground-500">Preço mín. c/ impostos s/IPI:</span>{' '}
                <span className="font-mono text-foreground-800">{formatCurrency(item.precoComImpostosMinimo)}</span>
              </div>
              <div>
                <span className="text-foreground-500">Tabela CIGAM NET:</span>{' '}
                <span className="font-mono text-foreground-800">{item.precoTabelaNet == null ? 'Consultar' : `${item.moedaTabela} ${formatCurrency(item.precoTabelaNet)}`}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-foreground-500">Margem tabela:</span>
                {item.margemTabela == null ? <span>Consultar</span> : <MargemGauge value={item.margemTabela} alvo={margemAlvo} showValue={canSeeSensitive} />}
              </div>
              <div>
                <span className="text-foreground-500">Equiv. USD:</span>{' '}
                <span className="font-mono font-semibold text-foreground-950">
                  US$ {formatCurrency(item.precoUnitarioUsd)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-xs text-foreground-500">Peso:</span>
              <PesoInput
                value={item.peso}
                onChange={(value) => onUpdatePeso(idx, value)}
                className="w-24 px-2 py-1.5 text-sm font-mono font-semibold text-foreground-950 bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50"
              />
              <span className="text-xs font-semibold text-foreground-500">kg</span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-foreground-500">NET sem impostos:</span>
                <NegotiatedPriceInput
                  value={item.precoNegociado}
                  onChange={(value) => onUpdatePrecoNegociado(idx, value)}
                  className="w-24 px-2 py-1.5 text-sm font-mono font-semibold text-foreground-950 bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500/50"
                />
              </div>
              <div className="text-xs">
                <span className="text-foreground-500">NET com impostos:</span>{' '}
                <span className="font-mono font-semibold text-foreground-950">{formatCurrency(item.precoNegociadoComImpostos)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-foreground-500">Margem:</span>
                <MargemGauge value={item.margemRealizada} alvo={margemAlvo} showValue={canSeeSensitive} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
